import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiGetGroupedBillingSync, apiSetGroupedBillingSource } from 'api';
import type { APIGroupedBillingSyncData } from 'types/api.types';

interface UseGroupedBillingSyncOptions {
  enabled?: boolean;
  policyGroupId?: string | null;
}

export const useGroupedBillingSync = (
  policyUuid: string | undefined,
  options: UseGroupedBillingSyncOptions = {},
) => {
  const queryClient = useQueryClient();
  const { enabled = true, policyGroupId } = options;

  const shouldFetch = !!policyUuid && enabled && !!policyGroupId;

  const { data, isLoading } = useQuery<APIGroupedBillingSyncData>({
    queryKey: ['groupedBillingSync', policyUuid],
    queryFn: () => apiGetGroupedBillingSync(policyUuid!),
    enabled: shouldFetch,
  });

  const setGroupedBillingSourceMutation = useMutation({
    mutationFn: (sourcePolicyUuid: string | null) =>
      apiSetGroupedBillingSource(policyUuid!, sourcePolicyUuid),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['groupedBillingSync', policyUuid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus'],
      });
    },
  });

  return {
    policyGroupId: data?.policy_group_id || policyGroupId || null,
    billingSourcePolicyUuid: data?.billing_source_policy_uuid || null,
    linkedPolicies: data?.linked_policies || [],
    carrierName: data?.carrier_name || null,
    isBillingSynced: !!data?.billing_source_policy_uuid,
    isInMultiBinderGroup: !!policyGroupId || !!data?.policy_group_id,
    isLoading,
    setGroupedBillingSource: setGroupedBillingSourceMutation.mutate,
    setGroupedBillingSourceAsync: setGroupedBillingSourceMutation.mutateAsync,
    isSettingGroupedBillingSource: setGroupedBillingSourceMutation.isPending,
  };
};
